import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase.js";
import Sidebar from "./Sidebar";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/Search.css";
import "../../css/HomePage.css";
import "../../css/DocumentationMainPage.css";

const BASE_URL = "https://bluebirddocumentationadmin.pythonanywhere.com";

const SOURCE_LABELS = {
    personal: "Personal",
    workspace: "Team Workspace",
    walkthrough: "Walkthrough",
    workspace_walkthrough: "Workspace Walkthrough",
};

const SOURCE_ICONS = {
    personal: "fa-user",
    workspace: "fa-users",
    walkthrough: "fa-book-open",
    workspace_walkthrough: "fa-book-open",
};

const TYPE_ICONS = {
    space: "fa-folder",
    page: "fa-file-alt",
    walkthrough: "fa-book-open",
};

function Search() {
    const navigate = useNavigate();
    const { enhancedRightClickEnabled } = useQuickLinks();

    const [userUid, setUserUid] = useState(null);

    const [query, setQuery] = useState("");
    const [searchIn, setSearchIn] = useState({ title: true, description: true, content: false });
    const [docType, setDocType] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [updatedFrom, setUpdatedFrom] = useState("");
    const [updatedTo, setUpdatedTo] = useState("");
    const [showDateFilters, setShowDateFilters] = useState(false);

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [resultTotal, setResultTotal] = useState(0);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserUid(user ? user.uid : null);
        });
        return () => unsubscribe();
    }, []);

    const toggleSearchIn = (field) => {
        setSearchIn((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        const selectedFields = Object.entries(searchIn)
            .filter(([, v]) => v)
            .map(([k]) => k);
        if (selectedFields.length === 0) {
            setSearchError("Please select at least one field to search in.");
            return;
        }

        setLoading(true);
        setSearchError(null);
        setSearched(false);
        setResults([]);

        try {
            const params = new URLSearchParams({
                search_in: selectedFields.join(","),
                doc_type: docType,
                limit: "100",
            });
            if (query) params.set("q", query);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            if (updatedFrom) params.set("updated_from", updatedFrom);
            if (updatedTo) params.set("updated_to", updatedTo);

            const response = await fetch(`${BASE_URL}/public-documentation/search/?${params}`, {
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || "Search failed");
            }

            const data = await response.json();
            setResults(data.results || []);
            setResultTotal(data.total ?? (data.results || []).length);
            setSearched(true);
        } catch (err) {
            setSearchError(err.message || "An error occurred during search.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setQuery("");
        setSearchIn({ title: true, description: true, content: false });
        setDocType("all");
        setDateFrom("");
        setDateTo("");
        setUpdatedFrom("");
        setUpdatedTo("");
        setResults([]);
        setSearched(false);
        setSearchError(null);
        setResultTotal(0);
    };

    const handleResultClick = (result) => {
        if (result.source === "personal") {
            const type = result.type === "space" ? "Space" : "Page";
            window.open(
                `https://bluebird-documentation.com/documentation/${type}/${result.name}/${result.author}/`,
                "_blank"
            );
        } else if (result.source === "workspace") {
            const type = result.type === "space" ? "Space" : "Page";
            window.open(
                `https://public.bluebird-documentation.com/docs/${type}/${result.author}/${result.name}?workspace_id=${result.workspace_id}&doc_id=${result.id}`,
                "_blank"
            );
        } else if (result.source === "walkthrough") {
            navigate(`/walkthroughs/${result.name}/${result.author}`);
        } else if (result.source === "workspace_walkthrough" && result.workspace_domain) {
            navigate(`/workspace/${result.workspace_domain}/walkthroughs/${result.id}`);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric", month: "short", day: "numeric",
        });
    };

    const loggedOutOptions = [
        { label: "Login", icon: <i className="fas fa-sign-in-alt"></i>, onClick: () => navigate("/login") },
        { label: "Register", icon: <i className="fas fa-user-plus"></i>, onClick: () => navigate("/register") },
        { label: "Try My Notebook", icon: <i className="fas fa-book"></i>, onClick: () => navigate("/my-notebook") },
        { label: "Visit YouTube Channel", icon: <i className="fab fa-youtube"></i>, onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer") },
        { label: "Join Discord", icon: <i className="fab fa-discord"></i>, onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer") },
    ];

    const contextMenuOptions = [
        { label: "Your Documents", url: "/documentation", onClick: () => navigate("/documentation") },
        { label: "Your Walkthroughs", url: "/walkthroughs", onClick: () => navigate("/walkthroughs") },
        { label: "Your Notebook", url: "/my-notebook", onClick: () => navigate("/my-notebook") },
        { label: "Your Workspace", url: "/workspaces", onClick: () => navigate("/workspaces") },
        { label: "Account Settings", url: `/account/${userUid}`, onClick: () => navigate(`/account/${userUid}`) },
    ];

    const menuOptions = userUid ? contextMenuOptions : loggedOutOptions;
    const { ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

    return (
        <div
            className="DocumentationMainPage__div"
            onContextMenu={enhancedRightClickEnabled ? handleContextMenu : undefined}
        >
            <Sidebar />

            <div className="DocumentationMainPage__main-content PublicSearch__content">
                <div className="PublicSearch__header">
                    <h1>Public Documentation Search</h1>
                    <p className="PublicSearch__subtitle">
                        Search across all public spaces, pages, and walkthroughs shared by the community.
                    </p>
                </div>

                <form onSubmit={handleSearch} className="PublicSearch__form">
                    <div className="PublicSearch__row">
                        <div className="PublicSearch__field PublicSearch__field--wide">
                            <label className="PublicSearch__label">Search Query</label>
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Enter keywords... (leave empty to browse all public documents)"
                                className="PublicSearch__input"
                                autoFocus
                            />
                        </div>
                        <div className="PublicSearch__field">
                            <label className="PublicSearch__label">Document Category</label>
                            <select
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                className="PublicSearch__select"
                            >
                                <option value="all">All Categories</option>
                                <option value="personal">Personal Documents</option>
                                <option value="workspace">Team Workspace Documents</option>
                                <option value="walkthrough">Walkthroughs</option>
                            </select>
                        </div>
                    </div>

                    <div className="PublicSearch__row">
                        <div className="PublicSearch__field">
                            <label className="PublicSearch__label">Search In</label>
                            <div className="PublicSearch__checkboxes">
                                {[
                                    { key: "title", label: "Title" },
                                    { key: "description", label: "Description" },
                                    { key: "content", label: "Content", note: "(slower)" },
                                ].map(({ key, label, note }) => (
                                    <label key={key} className="PublicSearch__checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={searchIn[key]}
                                            onChange={() => toggleSearchIn(key)}
                                        />
                                        <span>{label}</span>
                                        {note && <span className="PublicSearch__note">{note}</span>}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="PublicSearch__field PublicSearch__field--toggle">
                            <button
                                type="button"
                                className="PublicSearch__toggle-dates"
                                onClick={() => setShowDateFilters((v) => !v)}
                            >
                                <i className={`fas fa-chevron-${showDateFilters ? "up" : "down"}`}></i>
                                Date Filters
                            </button>
                        </div>
                    </div>

                    {showDateFilters && (
                        <>
                            <div className="PublicSearch__section-title">Created Date Range</div>
                            <div className="PublicSearch__row">
                                <div className="PublicSearch__field">
                                    <label className="PublicSearch__label">Created After</label>
                                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="PublicSearch__input" />
                                </div>
                                <div className="PublicSearch__field">
                                    <label className="PublicSearch__label">Created Before</label>
                                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="PublicSearch__input" />
                                </div>
                                <div className="PublicSearch__field">
                                    <label className="PublicSearch__label">Last Updated After</label>
                                    <input type="date" value={updatedFrom} onChange={(e) => setUpdatedFrom(e.target.value)} className="PublicSearch__input" />
                                </div>
                                <div className="PublicSearch__field">
                                    <label className="PublicSearch__label">Last Updated Before</label>
                                    <input type="date" value={updatedTo} onChange={(e) => setUpdatedTo(e.target.value)} className="PublicSearch__input" />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="PublicSearch__actions">
                        <button type="submit" className="PublicSearch__search-btn" disabled={loading}>
                            {loading
                                ? <><i className="fas fa-spinner fa-spin"></i> Searching...</>
                                : <><i className="fas fa-search"></i> Search</>
                            }
                        </button>
                        <button type="button" className="PublicSearch__clear-btn" onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </form>

                {searchError && (
                    <div className="PublicSearch__error">
                        <i className="fas fa-exclamation-circle"></i> {searchError}
                    </div>
                )}

                {searched && !loading && (
                    <div className="PublicSearch__results">
                        <div className="PublicSearch__results-header">
                            <span className="PublicSearch__results-count">
                                {results.length === 0
                                    ? "No results found"
                                    : resultTotal > results.length
                                        ? `Showing ${results.length} of ${resultTotal} results`
                                        : `${results.length} result${results.length !== 1 ? "s" : ""} found`
                                }
                            </span>
                        </div>

                        {results.length > 0 && (
                            <ul className="PublicSearch__results-list">
                                {results.map((result, idx) => (
                                    <li
                                        key={idx}
                                        className="PublicSearch__result-item"
                                        onClick={() => handleResultClick(result)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === "Enter" && handleResultClick(result)}
                                    >
                                        <div className="PublicSearch__result-header">
                                            <div className="PublicSearch__result-title-row">
                                                <span className={`PublicSearch__source-badge PublicSearch__source-badge--${result.source}`}>
                                                    <i className={`fas ${SOURCE_ICONS[result.source] || "fa-file"}`}></i>
                                                    {SOURCE_LABELS[result.source] || result.source}
                                                </span>
                                                <span className={`PublicSearch__type-badge PublicSearch__type-badge--${result.type}`}>
                                                    <i className={`fas ${TYPE_ICONS[result.type] || "fa-file"}`}></i>
                                                    {result.type === "space" ? "Space" : result.type === "page" ? "Page" : "Walkthrough"}
                                                </span>
                                                <span className="PublicSearch__result-name">{result.name}</span>
                                            </div>
                                            <div className="PublicSearch__result-meta">
                                                <span>
                                                    <i className="fas fa-user"></i> {result.author_name}
                                                </span>
                                                {result.workspace_name && (
                                                    <span>
                                                        <i className="fas fa-building"></i> {result.workspace_name}
                                                    </span>
                                                )}
                                                <span>Created: {formatDate(result.created_at)}</span>
                                                <span>Updated: {formatDate(result.updated_at)}</span>
                                                {result.views != null && (
                                                    <span><i className="fas fa-eye"></i> {result.views} views</span>
                                                )}
                                            </div>
                                        </div>
                                        {result.description && (
                                            <p className="PublicSearch__result-description">{result.description}</p>
                                        )}
                                        {result.content_snippet && (
                                            <p className="PublicSearch__result-snippet">
                                                <i className="fas fa-quote-left"></i> {result.content_snippet}
                                            </p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default Search;
